import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../lib/api';
import { Id } from '../convex/_generated/dataModel';

export default function OrganizerDashboard() {
  const navigate = useNavigate();
  const [organizerId, setOrganizerId] = useState<Id<"players"> | null>(null);

  // Get organizer ID from localStorage
  useEffect(() => {
    const storedPlayerId = localStorage.getItem('playerId');
    const role = localStorage.getItem('playerRole');
    
    if (!storedPlayerId || role !== 'organizer') {
      navigate('/login');
    } else {
      setOrganizerId(storedPlayerId as Id<"players">);
    }
  }, [navigate]);

  // Fetch organizer data
  const currentUser = useQuery(api.auth.getCurrentUser, 
    organizerId ? { playerId: organizerId } : 'skip'
  );

  const activeTournaments = useQuery(api.organizerDashboard.getActiveTournaments,
    organizerId ? { organizerId } : 'skip'
  );

  const completedTournaments = useQuery(api.organizerDashboard.getCompletedTournaments,
    organizerId ? { organizerId } : 'skip'
  );

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      open: { bg: 'bg-green-100', text: 'text-green-800', label: 'Abierto' },
      locked: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Bloqueado' },
      completed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Completado' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelado' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.open;
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (!currentUser || !organizerId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="/torneopadel.png" 
              alt="TorneoPadel" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Panel de Organizador</h1>
              <p className="text-sm text-gray-600">Hola, {currentUser.name}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Link
              to="/create"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Crear Torneo
            </Link>
            <Link
              to="/profile"
              className="text-gray-600 hover:text-gray-900"
            >
              {currentUser.profilePicture ? (
                <img 
                  src={currentUser.profilePicture} 
                  alt="Profile" 
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {currentUser.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Active Tournaments */}
        {activeTournaments && activeTournaments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Torneos Activos</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {activeTournaments.map((tournament: any) => (
                <div key={tournament._id} className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{tournament.name}</h3>
                      <p className="text-gray-600">{tournament.club}</p>
                    </div>
                    {getStatusBadge(tournament.status)}
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-gray-700">
                      <span className="font-medium">Fecha:</span> {formatDate(tournament.date)}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Hora:</span> {tournament.time}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Formato:</span> {tournament.format}
                    </p>
                    <p className="text-gray-700">
                      <span className="font-medium">Jugadores:</span> {tournament.confirmedCount}/{tournament.maxPlayers}
                      {tournament.waitlistCount > 0 && (
                        <span className="text-yellow-600 ml-2">
                          (+{tournament.waitlistCount} en espera)
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex space-x-2">
                    <Link
                      to={`/manage/${tournament._id}`}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
                    >
                      Gestionar
                    </Link>
                    <Link
                      to={`/tournament/${tournament.inviteCode}`}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-center"
                    >
                      Ver
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Tournament CTA if no active tournaments */}
        {(!activeTournaments || activeTournaments.length === 0) && (
          <div className="mb-8 bg-white rounded-lg shadow-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No tienes torneos activos</h2>
            <p className="text-gray-600 mb-6">
              Crea tu primer torneo y comienza a gestionar jugadores
            </p>
            <Link
              to="/create"
              className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Crear Torneo
            </Link>
          </div>
        )}

        {/* Completed Tournaments */}
        {completedTournaments && completedTournaments.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Torneos Pasados</h2>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Torneo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Club
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Jugadores
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {completedTournaments.map((tournament: any) => (
                    <tr key={tournament._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{tournament.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{tournament.club}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {formatDate(tournament.date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{tournament.playerCount}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(tournament.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/manage/${tournament._id}`}
                          className="text-blue-600 hover:text-blue-900 text-sm"
                        >
                          Ver detalles
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}